import { useApiKeys } from "@/features/api-keys/queries";
import { useCurrentProject } from "@/features/projects/current-project";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, Info } from "lucide-react";
import type { ReactNode } from "react";
import { useModels, useRevisions } from "../models/queries";

function hasSessionSecret(keyId: string): boolean {
  return !!sessionStorage.getItem(`autolab:secret:${keyId}`);
}

interface CellProps {
  label: string;
  value: string;
  empty?: string;
  info?: string;
  children: ReactNode;
}

function Cell({ label, value, empty, info, children }: CellProps) {
  return (
    <div className="relative">
      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            type="button"
            className="flex w-full flex-col gap-0.5 rounded-lg border border-border bg-surface px-4 py-3 text-left transition-colors hover:border-border-strong hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
          >
            <span className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-fg-dim">
              {label}
              {info && <span aria-hidden="true" className="inline-block w-3" />}
            </span>
            <span className="flex items-center justify-between gap-2">
              <span
                className={`text-sm font-medium truncate ${value ? "text-fg" : "text-fg-dim italic"}`}
              >
                {value || empty || "—"}
              </span>
              <ChevronDown size={12} className="shrink-0 text-fg-dim" />
            </span>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            sideOffset={4}
            align="start"
            className="z-50 min-w-[220px] rounded-lg border border-border bg-surface shadow-lg"
          >
            {children}
            <Popover.Arrow className="fill-border" />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {info && (
        <Popover.Root>
          <Popover.Trigger asChild>
            <button
              type="button"
              aria-label={`About ${label}`}
              className="absolute top-3 left-[calc(1rem+var(--info-offset,0px))] inline-flex cursor-help items-center text-fg-dim hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
              style={
                {
                  // Position the info icon after the eyebrow label text — the
                  // label is small uppercase tracked text, so reserving ~12ch
                  // leaves room. Designers may tune via the CSS variable.
                  "--info-offset": `${Math.max(label.length, 4) * 0.55 + 0.5}rem`,
                } as React.CSSProperties
              }
            >
              <Info size={11} />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              sideOffset={4}
              align="start"
              className="z-50 max-w-[280px] rounded-lg border border-border bg-surface p-3 text-xs normal-case tracking-normal text-fg-muted shadow-lg"
            >
              {info}
              <Popover.Arrow className="fill-border" />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}
    </div>
  );
}

interface TargetBarProps {
  modelId: string | null;
  revisionNo: number | null;
  apiKeyId: string | null;
  onModelChange: (id: string) => void;
  onRevisionChange: (no: number) => void;
  onApiKeyChange: (id: string) => void;
}

export function TargetBar({
  modelId,
  revisionNo,
  apiKeyId,
  onModelChange,
  onRevisionChange,
  onApiKeyChange,
}: TargetBarProps) {
  const { projectId } = useCurrentProject();
  const { data: models } = useModels(projectId);
  const { data: revisions } = useRevisions(modelId ?? "");
  const { data: apiKeys } = useApiKeys(projectId);

  const selectedModel = models?.items.find((m) => m.id === modelId);
  const selectedRevision = revisions?.items.find((r) => r.revision_no === revisionNo);
  const selectedKey = apiKeys?.items.find((k) => k.id === apiKeyId);

  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: "1.4fr 1fr 1fr" }}>
      {/* Model cell */}
      <Cell label="Model" value={selectedModel?.name ?? ""} empty="Select a model">
        <div className="max-h-64 overflow-y-auto p-1.5">
          {!models?.items.length && (
            <div className="px-3 py-2 text-xs text-fg-muted">No models</div>
          )}
          {models?.items.map((m) => (
            <Popover.Close key={m.id} asChild>
              <button
                type="button"
                className={`w-full rounded-md px-3 py-2 text-left text-xs transition-colors hover:bg-surface-2 ${m.id === modelId ? "bg-surface-2 font-medium text-fg" : "text-fg-muted"}`}
                onClick={() => onModelChange(m.id)}
              >
                <div className="font-medium text-fg">{m.name}</div>
                <div className="text-fg-dim">
                  {m.task_type}
                  {m.active_revision_id
                    ? ` · active ${m.active_revision_id.slice(0, 8)}…`
                    : " · no active"}
                </div>
              </button>
            </Popover.Close>
          ))}
        </div>
      </Cell>

      {/* Revision cell */}
      <Cell
        label="Revision"
        value={
          revisionNo != null
            ? `#${revisionNo}${selectedRevision?.status === "active" ? " (active)" : ""}`
            : ""
        }
        empty={modelId ? "Select revision" : "Select model first"}
      >
        <div className="max-h-64 overflow-y-auto p-1.5">
          {!modelId && <div className="px-3 py-2 text-xs text-fg-muted">Select a model first</div>}
          {modelId && !revisions?.items.length && (
            <div className="px-3 py-2 text-xs text-fg-muted">No revisions</div>
          )}
          {revisions?.items.map((r) => (
            <Popover.Close key={r.revision_no} asChild>
              <button
                type="button"
                className={`w-full rounded-md px-3 py-2 text-left text-xs transition-colors hover:bg-surface-2 ${r.revision_no === revisionNo ? "bg-surface-2 font-medium text-fg" : "text-fg-muted"}`}
                onClick={() => onRevisionChange(r.revision_no)}
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono font-medium text-fg">#{r.revision_no}</span>
                  {r.status === "active" && (
                    <span className="rounded-sm bg-accent/10 px-1 py-0.5 text-[10px] font-medium text-accent">
                      active
                    </span>
                  )}
                  <span className="ml-auto text-fg-dim">
                    {Object.entries(r.metrics ?? {})
                      .slice(0, 1)
                      .map(([k, v]) => `${k}: ${typeof v === "number" ? v.toFixed(3) : v}`)
                      .join("")}
                  </span>
                </div>
              </button>
            </Popover.Close>
          ))}
        </div>
      </Cell>

      {/* API Key cell */}
      <Cell
        label="API Key"
        value={selectedKey ? `${selectedKey.name} (${selectedKey.key_prefix}…)` : ""}
        empty="Paste secret or pick saved key"
        info="Secrets are only shown once when a key is created. The picker lists keys whose secrets you saved in this browser session. Otherwise, paste the secret directly in the Request panel."
      >
        <div className="max-h-64 overflow-y-auto p-1.5">
          {(() => {
            const saved = (apiKeys?.items ?? []).filter(
              (k) => !k.revoked_at && hasSessionSecret(k.id),
            );
            if (!saved.length) {
              return (
                <div className="px-3 py-2 text-xs text-fg-muted">
                  No saved keys in this session. Create a key or paste its secret in the Request
                  panel.
                </div>
              );
            }
            return saved.map((k) => (
              <Popover.Close key={k.id} asChild>
                <button
                  type="button"
                  className={`w-full rounded-md px-3 py-2 text-left text-xs transition-colors hover:bg-surface-2 ${k.id === apiKeyId ? "bg-surface-2 font-medium text-fg" : "text-fg-muted"}`}
                  onClick={() => onApiKeyChange(k.id)}
                >
                  <div className="font-medium text-fg">{k.name}</div>
                  <div className="font-mono text-fg-dim">{k.key_prefix}…</div>
                </button>
              </Popover.Close>
            ));
          })()}
        </div>
      </Cell>
    </div>
  );
}
